import { useMemo, useState } from "react";
import { categories, events, heatColor, type Trend } from "@/mocks/guaData";

function TrendMark({ trend }: { trend: Trend }) {
  if (trend === "up") return <i className="ri-arrow-up-line text-[13px] text-primary-600"></i>;
  if (trend === "down") return <i className="ri-arrow-down-line text-[13px] text-secondary-600"></i>;
  return <i className="ri-subtract-line text-[13px] text-foreground-500"></i>;
}

export default function EventFeed() {
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  const list = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return events.filter((e) => {
      const okCat = cat === "all" || e.cat === cat;
      const okKw =
        !kw ||
        e.title.toLowerCase().includes(kw) ||
        e.summary.toLowerCase().includes(kw) ||
        e.tags.some((t) => t.toLowerCase().includes(kw));
      return okCat && okKw;
    });
  }, [cat, q]);

  return (
    <section id="feed" className="scroll-mt-24 bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-accent-600">
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                <i className="ri-stack-line text-[13px]"></i>
              </span>
              瓜田情报站
            </p>
            <h2 className="mt-3 text-2xl font-bold text-foreground-950 md:text-4xl">按分类，翻翻今天的瓜</h2>
            <p className="mt-2 text-sm text-foreground-600 md:text-base">
              影视、综艺、词条、品牌、音乐与社会热议一网打尽，输入关键词或切换分类，快速定位你想吃的那一块。
            </p>
          </div>

          <div className="relative w-full md:w-72">
            <span className="pointer-events-none absolute top-1/2 left-3 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-[15px]"></i>
            </span>
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="搜索事件 / 关键词"
              aria-label="搜索事件"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pr-3 pl-9 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
            />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setCat("all")}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
              cat === "all"
                ? "bg-primary-500 font-medium text-background-50"
                : "bg-background-50 text-foreground-700 hover:bg-secondary-100"
            }`}
          >
            全部瓜田
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCat(c.id)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                cat === c.id
                  ? "bg-primary-500 font-medium text-background-50"
                  : "bg-background-50 text-foreground-700 hover:bg-secondary-100"
              }`}
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={`${c.icon} text-[15px]`}></i>
              </span>
              {c.label}
            </button>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-between text-sm text-foreground-600">
          <p>
            共 <span className="font-semibold text-primary-600">{list.length}</span> 条相关事件
          </p>
          {q && (
            <button
              type="button"
              onClick={() => {
                setQ("");
                setCat("all");
              }}
              className="inline-flex items-center gap-1 whitespace-nowrap text-primary-600 hover:text-primary-700"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-refresh-line text-[14px]"></i>
              </span>
              重置筛选
            </button>
          )}
        </div>

        {list.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {list.map((ev) => (
              <article
                key={ev.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
              >
                <div className="relative aspect-[10/7] overflow-hidden bg-background-200">
                  <img
                    src={ev.cover}
                    alt={`${ev.title} 热门瓜田事件封面`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute top-3 left-3 rounded-md bg-background-50/95 px-2.5 py-1 text-xs font-medium text-foreground-800">
                    {ev.catLabel}
                  </span>
                  {ev.featured && (
                    <span className="absolute top-3 right-3 flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-2 py-1 text-[11px] font-semibold text-background-50">
                      <i className="ri-vip-crown-2-line text-[13px]"></i>专题
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="text-sm leading-snug font-semibold text-foreground-950 group-hover:text-primary-700 md:text-base">
                    {ev.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">{ev.summary}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {ev.tags.map((t) => (
                      <span key={t} className="rounded bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                        #{t}
                      </span>
                    ))}
                  </div>
                  <div className="mt-auto flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <span className={`font-heading text-base ${heatColor(ev.heat)}`}>{ev.heat}</span>
                      <TrendMark trend={ev.trend} />
                      <span className="ml-1 whitespace-nowrap">{ev.time.replace("2026-09-0", "09-0")}</span>
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
              <i className="ri-inbox-2-line text-2xl"></i>
            </span>
            <p className="mt-4 text-base font-semibold text-foreground-800">暂时没翻到这块瓜</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词或分类试试，好瓜总是在路上。</p>
            <button
              type="button"
              onClick={() => {
                setQ("");
                setCat("all");
              }}
              className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-refresh-line text-[14px]"></i>
              </span>
              看看全部
            </button>
          </div>
        )}
      </div>
    </section>
  );
}